import { countries } from "country-data-list";
import { z } from "zod";

// country-data-list attaches alpha2-keyed lookup at runtime but its TS types
// only expose the array. This cast gives us a typed lookup interface.
const countryByCode = countries as unknown as Record<
  string,
  { name: string; emoji?: string } | undefined
>;

// Which countries the Whop payout rail serves is NOT a list in this file. It is
// operational policy owned by the `whop-payouts-enabled` PostHog flag's release
// conditions, editable without a deploy, exactly like `platform-access-blocked`
// owns platform access. A hardcoded copy here used to be ANDed with that flag,
// so a flag rolled out to 100% silently did nothing for the countries the copy
// omitted (Nigeria being the case that surfaced it). See @repo/api's
// payout-rail util.

// Every ISO alpha-2 code, so enrichment countries outside any payout rail pass.
export const FULL_ISO_ALPHA2_SET: ReadonlySet<string> = new Set(
  (countries.all as { alpha2?: unknown }[])
    .map((c) => (typeof c.alpha2 === "string" ? c.alpha2.toUpperCase() : ""))
    .filter((code) => code.length === 2),
);

// NULL for anything not a recognized alpha-2 code (unknown passes downstream).
export function normalizeFullIsoAlpha2(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const code = value.trim().toUpperCase();
  return FULL_ISO_ALPHA2_SET.has(code) ? code : null;
}

// country-data-list reports the ISO register's own names, which are sometimes
// out of date ("Turkey": the UN accepted the rename to Türkiye in 2022) or
// phrased as a register entry rather than a country ("Korea, Republic Of",
// "VietNam"). Corrected once here, in the shared resolver, rather than in each
// surface that renders a country.
//
// Not the only country label in the product: format-hard-filter.ts renders
// saved filter chips through Intl.DisplayNames, which returns CLDR names and
// so still disagrees with this table on a handful of codes.
const COUNTRY_NAME_OVERRIDES: Record<string, string> = {
  BN: "Brunei",
  BO: "Bolivia",
  CD: "Democratic Republic of the Congo",
  CG: "Republic of the Congo",
  FM: "Micronesia",
  IR: "Iran",
  KP: "North Korea",
  KR: "South Korea",
  LA: "Laos",
  PS: "Palestinian Territories",
  SH: "Saint Helena",
  SY: "Syria",
  TL: "Timor-Leste",
  TR: "Türkiye",
  TZ: "Tanzania",
  VA: "Vatican City",
  VE: "Venezuela",
  VG: "British Virgin Islands",
  VI: "U.S. Virgin Islands",
  VN: "Vietnam",
};

// Names still in wide enough use that someone will type them into a search box,
// or that an integration may send. Matched only, never displayed. The register
// name an override replaced is added automatically by getCountrySearchTerms, so
// it does not belong here, and accents are folded before comparing, so
// "Turkiye" reaches "Türkiye" without an entry either.
//
// Every alias must identify exactly one country. Ambiguous ones ("Congo",
// "Korea") are deliberately absent: an alias also resolves inbound filter
// values to a single code, and picking the wrong Congo silently retargets a
// study.
const COUNTRY_NAME_ALIASES: Record<string, readonly string[]> = {
  AE: ["UAE"],
  CD: ["DR Congo", "Congo-Kinshasa", "Zaire"],
  CG: ["Congo-Brazzaville"],
  CI: ["Ivory Coast"],
  CV: ["Cape Verde"],
  CZ: ["Czechia"],
  GB: ["Great Britain", "Britain", "UK"],
  MM: ["Burma"],
  MO: ["Macau"],
  NL: ["Holland"],
  PS: ["Palestine"],
  SZ: ["Swaziland"],
  TL: ["East Timor"],
  US: ["USA", "America"],
  VA: ["Holy See"],
  // Folding collapses runs of whitespace but does not remove it, so the
  // register's "VietNam" does not match this on its own.
  VN: ["Viet Nam"],
};

export function getCountryDisplayName(countryCode: string | null): string {
  if (!countryCode) {
    return "";
  }
  const code = countryCode.toUpperCase();
  return (
    COUNTRY_NAME_OVERRIDES[code] ?? countryByCode[code]?.name ?? countryCode
  );
}

/**
 * Names a country can be matched by, beyond the display name its callers
 * already compare against: the ISO code, the register name an override
 * replaced, and any alias. Comparison is the caller's job (see
 * `matchesSearchText`), so case and accents do not matter here.
 *
 * Deriving the replaced register name rather than listing it keeps the two
 * tables from drifting: adding an override can no longer silently stop the old
 * name from resolving.
 */
export function getCountrySearchTerms(countryCode: string): string[] {
  const code = countryCode.toUpperCase();
  const registerName = countryByCode[code]?.name;
  const replaced =
    registerName && registerName !== getCountryDisplayName(code)
      ? [registerName]
      : [];
  return [code, ...replaced, ...(COUNTRY_NAME_ALIASES[code] ?? [])];
}

export function getCountryFlag(countryCode: string): string {
  return countryByCode[countryCode.toUpperCase()]?.emoji ?? "";
}

// The canonical list of countries supported by Virtual Incentives (PayPal) for
// cashout, minus the sanctioned floor. VI is the fallback rail, so this list is
// a real vendor capability and stays in code; the Whop rail's coverage is flag
// policy instead (see the note at the top of this file).
// See: Virtual Incentives program 60319 (international PayPal) country list.
export const VI_SUPPORTED_COUNTRIES = [
  // Africa
  "DZ", // Algeria
  "AO", // Angola
  "BJ", // Benin
  "BW", // Botswana
  "BF", // Burkina Faso
  "BI", // Burundi
  "CM", // Cameroon
  "CV", // Cape Verde
  "TD", // Chad
  "KM", // Comoros
  "CI", // Cote d'Ivoire
  "CG", // Republic of the Congo
  "DJ", // Djibouti
  "EG", // Egypt
  "ER", // Eritrea
  "ET", // Ethiopia
  "GA", // Gabon Republic
  "GM", // Gambia
  "GN", // Guinea
  "GW", // Guinea-Bissau
  "KE", // Kenya
  "LS", // Lesotho
  "MG", // Madagascar
  "MW", // Malawi
  "ML", // Mali
  "MR", // Mauritania
  "MU", // Mauritius
  "MA", // Morocco
  "MZ", // Mozambique
  "NA", // Namibia
  "NE", // Niger
  "NG", // Nigeria
  "RW", // Rwanda
  "SH", // Saint Helena
  "ST", // Sao Tome and Principe
  "SN", // Senegal
  "SC", // Seychelles
  "SL", // Sierra Leone
  "SO", // Somalia
  "ZA", // South Africa
  "SZ", // Swaziland
  "TZ", // Tanzania
  "TG", // Togo
  "TN", // Tunisia
  "UG", // Uganda
  "ZM", // Zambia
  "ZW", // Zimbabwe

  // Americas
  "AI", // Anguilla
  "AG", // Antigua and Barbuda
  "AR", // Argentina
  "AW", // Aruba
  "BS", // Bahamas
  "BB", // Barbados
  "BZ", // Belize
  "BM", // Bermuda
  "BO", // Bolivia
  "BR", // Brazil
  "VG", // British Virgin Islands
  "CA", // Canada
  "KY", // Cayman Islands
  "CL", // Chile
  "CO", // Colombia
  "CR", // Costa Rica
  "DM", // Dominica
  "DO", // Dominican Republic
  "EC", // Ecuador
  "SV", // El Salvador
  "FK", // Falkland Islands
  "GL", // Greenland
  "GD", // Grenada
  "GT", // Guatemala
  "GY", // Guyana
  "HN", // Honduras
  "JM", // Jamaica
  "MX", // Mexico
  "MS", // Montserrat
  "NI", // Nicaragua
  "PA", // Panama
  "PY", // Paraguay
  "PE", // Peru
  "KN", // Saint Kitts and Nevis
  "LC", // Saint Lucia
  "PM", // Saint Pierre and Miquelon
  "VC", // Saint Vincent and the Grenadines
  "SR", // Suriname
  "TT", // Trinidad and Tobago
  "TC", // Turks and Caicos
  "US", // United States
  "UY", // Uruguay
  "VE", // Venezuela

  // Asia Pacific
  "AM", // Armenia
  "AU", // Australia
  "BH", // Bahrain
  "BT", // Bhutan
  "BN", // Brunei
  "KH", // Cambodia
  "CN", // Mainland China
  "CK", // Cook Islands
  "FJ", // Fiji
  "PF", // French Polynesia
  "HK", // Hong Kong SAR, China
  "IN", // India
  "ID", // Indonesia
  "IL", // Israel
  "JP", // Japan
  "JO", // Jordan
  "KZ", // Kazakhstan
  "KI", // Kiribati
  "KW", // Kuwait
  "KG", // Kyrgyzstan
  "LA", // Laos
  "MY", // Malaysia
  "MV", // Maldives
  "MH", // Marshall Islands
  "FM", // Federated States of Micronesia
  "MN", // Mongolia
  "NR", // Nauru
  "NP", // Nepal
  "NC", // New Caledonia
  "NZ", // New Zealand
  "NU", // Niue
  "NF", // Norfolk Island
  "OM", // Oman
  "PW", // Palau
  "PG", // Papua New Guinea
  "PH", // Philippines
  "PN", // Pitcairn Islands
  "QA", // Qatar
  "WS", // Samoa
  "SA", // Saudi Arabia
  "SG", // Singapore
  "SB", // Solomon Islands
  "KR", // South Korea
  "LK", // Sri Lanka
  "TW", // Taiwan, China
  "TJ", // Tajikistan
  "TH", // Thailand
  "TO", // Tonga
  "TM", // Turkmenistan
  "TV", // Tuvalu
  "AE", // United Arab Emirates
  "VU", // Vanuatu
  "VN", // Vietnam
  "WF", // Wallis and Futuna
  "YE", // Yemen

  // Europe
  "AL", // Albania
  "AD", // Andorra
  "AT", // Austria
  "AZ", // Azerbaijan
  "BY", // Belarus
  "BE", // Belgium
  "BA", // Bosnia and Herzegovina
  "BG", // Bulgaria
  "HR", // Croatia
  "CY", // Cyprus
  "CZ", // Czech Republic
  "DK", // Denmark
  "EE", // Estonia
  "FO", // Faroe Islands
  "FI", // Finland
  "FR", // France
  "GE", // Georgia
  "DE", // Germany
  "GR", // Greece
  "HU", // Hungary
  "IS", // Iceland
  "IE", // Ireland
  "IT", // Italy
  "LV", // Latvia
  "LI", // Liechtenstein
  "LT", // Lithuania
  "LU", // Luxembourg
  "MT", // Malta
  "MD", // Moldova
  "MC", // Monaco
  "ME", // Montenegro
  "NL", // Netherlands
  "NO", // Norway
  "PL", // Poland
  "PT", // Portugal
  "RO", // Romania
  "RU", // Russia
  "SM", // San Marino
  "RS", // Serbia
  "SK", // Slovakia
  "SI", // Slovenia
  "ES", // Spain
  "SJ", // Svalbard and Jan Mayen
  "SE", // Sweden
  "CH", // Switzerland
  "UA", // Ukraine
  "GB", // United Kingdom
  "VA", // Vatican City
] as const;

export const VI_SUPPORTED_COUNTRY_SET = new Set<string>(VI_SUPPORTED_COUNTRIES);

// Sanctioned / high-risk countries. Hardcoded compliance floor: blocked from
// platform access and cashout in code, with no runtime override. Operational
// (non-legal) country blocking lives in the `platform-access-blocked` PostHog
// flag instead, where it can be edited without a deploy.
export const SANCTIONED_COUNTRIES = [
  "RU", // Russia
  "BY", // Belarus
  "IQ", // Iraq
  "LB", // Lebanon
  "MM", // Myanmar
  "PS", // Palestinian Territories
  "HT", // Haiti
  "VE", // Venezuela
] as const;

export const SANCTIONED_COUNTRY_SET = new Set<string>(SANCTIONED_COUNTRIES);

export function isSanctionedCountry(
  countryCode: string | null | undefined,
): boolean {
  if (!countryCode) {
    return false;
  }
  return SANCTIONED_COUNTRY_SET.has(countryCode.toUpperCase());
}

// Where Terac may operate at all: every recognized country except the
// sanctioned floor. This is the structural ceiling, not the live answer;
// which countries are actually open is operational policy owned by the
// `platform-access-blocked` PostHog flag, editable without a deploy. Code that
// needs "is Terac open here" must ask that flag (see @repo/api's user-access),
// never a vendor capability list such as VI_SUPPORTED_COUNTRIES or the
// Twilio/Linq SMS blocklists.
export const PLATFORM_COUNTRY_SET: ReadonlySet<string> = new Set(
  [...FULL_ISO_ALPHA2_SET].filter((code) => !SANCTIONED_COUNTRY_SET.has(code)),
);

export function isPlatformCountry(
  countryCode: string | null | undefined,
): boolean {
  if (!countryCode) {
    return false;
  }
  return PLATFORM_COUNTRY_SET.has(countryCode.toUpperCase());
}

// What a country picker should offer: everywhere Terac may operate, minus the
// codes ISO has retired ("SU" USSR, "YU" Yugoslavia, "AN" Netherlands Antilles)
// or reserved for non-countries ("EU"). PLATFORM_COUNTRY_SET keeps those so a
// value already stored against one still validates, but they are not audiences
// anyone can target. XK (Kosovo) is user-assigned rather than assigned and is
// kept deliberately. Sorted by display name so no caller re-sorts.
export const SELECTABLE_COUNTRY_CODES: readonly string[] = (
  countries.all as { alpha2?: unknown; status?: unknown }[]
)
  .filter((c) => c.status === "assigned" || c.status === "user assigned")
  .map((c) => (typeof c.alpha2 === "string" ? c.alpha2.toUpperCase() : ""))
  .filter((code) => PLATFORM_COUNTRY_SET.has(code))
  .sort((a, b) =>
    getCountryDisplayName(a).localeCompare(getCountryDisplayName(b)),
  );

// Accepts any country Terac could operate in. Per-country policy is enforced
// at runtime by the platform-access-blocked flag, so this schema deliberately
// does NOT narrow to the Whop payout allowlist because doing so used to reject
// profile updates from VI-rail countries like Nigeria.
export const countryCodeSchema = z
  .string()
  .length(2, "Country must be a 2-letter ISO code")
  .transform((val) => val.toUpperCase())
  .refine((val) => PLATFORM_COUNTRY_SET.has(val), "Unsupported country code");

// Cashout eligibility for the VI rail only, reflecting what VI can actually pay
// (capability), minus the sanctioned floor. Operational policy (which
// countries/users are blocked) is enforced separately via the
// `platform-access-blocked` flag in @repo/api's user-access util. The active
// rail is chosen by the `whop-payouts-enabled` flag (web) and by platform
// (native mobile uses VI); there is deliberately no isWhopCashoutEligible
// counterpart, because that flag already answers it.

// VI rail: everything Virtual Incentives (international PayPal) supports, minus
// sanctioned countries. VI pays countries Whop cannot (e.g. Nigeria), which is
// what lets an access-allowlisted user from a Whop-excluded country still cash
// out once the flag routes them here.
export const VI_CASHOUT_ELIGIBLE_COUNTRIES = VI_SUPPORTED_COUNTRIES.filter(
  (code) => !SANCTIONED_COUNTRY_SET.has(code),
);

export const VI_CASHOUT_ELIGIBLE_COUNTRY_SET = new Set<string>(
  VI_CASHOUT_ELIGIBLE_COUNTRIES,
);

export function isViCashoutEligible(
  countryCode: string | null | undefined,
): boolean {
  if (!countryCode) {
    return false;
  }
  return VI_CASHOUT_ELIGIBLE_COUNTRY_SET.has(countryCode.toUpperCase());
}

// US participants get the full VI reward catalog (PayPal, Venmo, Visa Prepaid,
// Direct Cashout). All other countries are routed through the international
// PayPal-only program (60319).
export function getPaymentMethodsForCountry(countryCode: string): string[] {
  return countryCode.toUpperCase() === "US"
    ? ["PayPal", "Venmo", "Visa Prepaid Card", "Direct Cashout"]
    : ["PayPal"];
}
